import { useState, FormEvent } from 'react';
import type { FlicButton } from '@shared/types';
import { Button } from '../ui/Button';
import { Card } from '../ui/Card';
import './ButtonManagement.css';

interface ButtonManagementProps {
  buttons: FlicButton[];
  onAssign: (serialNumber: string, buttonType: 'sideA' | 'sideB') => Promise<void>;
  onRemove: (buttonId: string) => Promise<void>;
  isAssigning?: boolean;
  isRemoving?: boolean;
}

export function ButtonManagement({
  buttons,
  onAssign,
  onRemove,
  isAssigning,
  isRemoving,
}: ButtonManagementProps) {
  const [serialNumber, setSerialNumber] = useState('');
  const [buttonType, setButtonType] = useState<'sideA' | 'sideB'>('sideA');
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!serialNumber.trim()) {
      setError('Serial number é obrigatório');
      return;
    }

    try {
      await onAssign(serialNumber.trim(), buttonType);
      setSerialNumber('');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao atribuir botão');
    }
  };

  return (
    <div className="button-management">
      <Card className="button-management__form-card">
        <h3 className="button-management__title">Atribuir Novo Botão</h3>
        <form onSubmit={handleSubmit} className="button-management__form">
          <div className="button-management__field">
            <label htmlFor="serialNumber" className="button-management__label">
              Serial Number *
            </label>
            <input
              id="serialNumber"
              type="text"
              className="button-management__input"
              value={serialNumber}
              onChange={(e) => setSerialNumber(e.target.value)}
              placeholder="Ex: BJ20-G12345"
              required
              disabled={isAssigning}
            />
          </div>

          <div className="button-management__field">
            <label htmlFor="buttonType" className="button-management__label">
              Tipo de Botão *
            </label>
            <select
              id="buttonType"
              className="button-management__select"
              value={buttonType}
              onChange={(e) => setButtonType(e.target.value as 'sideA' | 'sideB')}
              disabled={isAssigning}
            >
              <option value="sideA">Lado A</option>
              <option value="sideB">Lado B</option>
            </select>
          </div>

          {error && <div className="button-management__error">{error}</div>}

          <Button type="submit" disabled={isAssigning}>
            {isAssigning ? 'Atribuindo...' : 'Atribuir Botão'}
          </Button>
        </form>
      </Card>

      <div className="button-management__list">
        <h3 className="button-management__title">Botões Atribuídos</h3>
        {buttons.length === 0 ? (
          <Card>
            <p className="button-management__empty">Nenhum botão atribuído ainda</p>
          </Card>
        ) : (
          <div className="button-management__grid">
            {buttons.map((button) => (
              <Card key={button.id} className="button-management__button-card">
                <div className="button-management__button-info">
                  <div className="button-management__button-header">
                    <span className="button-management__button-serial">{button.serialNumber}</span>
                    <span
                      className={`button-management__button-badge button-management__button-badge--${button.buttonType}`}
                    >
                      {button.buttonType === 'sideA' ? 'Lado A' : 'Lado B'}
                    </span>
                  </div>
                  <div className="button-management__button-status">
                    <span className={`button-management__status-indicator ${button.isActive ? 'active' : 'inactive'}`}>
                      {button.isActive ? '🟢 Ativo' : '🔴 Inativo'}
                    </span>
                    {button.lastSeen && (
                      <span className="button-management__last-seen">
                        Visto: {new Date(button.lastSeen).toLocaleString('pt-BR')}
                      </span>
                    )}
                  </div>
                </div>
                <Button
                  variant="danger"
                  size="sm"
                  onClick={() => onRemove(button.id)}
                  disabled={isRemoving}
                >
                  {isRemoving ? 'Removendo...' : 'Remover'}
                </Button>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
